import React, { useState, useEffect } from 'react';
import {useDispatch} from 'react-redux';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    Alert,
    TextField
} from '@mui/material';
import { 
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { getMlmuserInfoByMobilenumberAPI } from '../../MlmuserAPI';
import { useSelector } from 'react-redux';
import {updateMlmuserInfo} from 'components/mlmuser/MlmuserAPI';
import { updateMlmuserObj } from 'store/slice/DmpSlice';
import { reconfirmPaymentAPI, updateAddressAPI } from 'api/CoreMlmmgmtWebServices';
import { setMlmUserObj } from 'store/slice/EcomSlice';

export default function PartnerActions() {
    const dispatch = useDispatch();
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [kitDeliveryDialogOpen, setKitDeliveryDialogOpen] = useState(false);
    const [tempKitDelivered, setTempKitDelivered] = useState(false);
    const [apiError, setApiError] = useState('');
    const [saving, setSaving] = useState(false);
    const [refreshingPayment, setRefreshingPayment] = useState(false);
    const [paymentRefreshError, setPaymentRefreshError] = useState(''); // Add new state for payment refresh errors
    const [editAddressDialogOpen, setEditAddressDialogOpen] = useState(false);
    const [addressForm, setAddressForm] = useState({ address: '', city: '', pincode: '' });
    const [addressSaving, setAddressSaving] = useState(false);
    const [addressError, setAddressError] = useState('');
    const {mlmUserObj} = useSelector((state)=>state.ecomslice.mlm);
    const { mlmuserList, productList } = useSelector((state) => state.dmpslice);

    useEffect(() => {
        // Initialize user info from mlmUserObj if available
        if (mlmUserObj && mlmUserObj.mobilenumber) {
            fetchUserInfo(mlmUserObj.mobilenumber);
        }
    }, [mlmUserObj]);

    const fetchUserInfo = (mobile) => {
        if (!mobile || mobile.trim().length < 10) {
            return;
        }

        setLoading(true);

        getMlmuserInfoByMobilenumberAPI(
            mobile,
            (response) => {
                console.log('MLM User Info API Response:', response);
                if (response && response.length > 0) {
                    setUserInfo(response[0]);
                } else {
                    setUserInfo(null);
                }
                setLoading(false);
            },
            () => {
                
            }
        );
    };

    const handleRefresh = () => {
        if (mlmUserObj && mlmUserObj.mobilenumber) {
            fetchUserInfo(mlmUserObj.mobilenumber);
        }
    };

    const handlePaymentRefresh = () => {
        if (!userInfo?.id && !userInfo?.userid) return;

        setRefreshingPayment(true);
        setPaymentRefreshError(''); // Clear any previous errors

        reconfirmPaymentAPI(
            { mlmUserId: userInfo.id},
            (response) => {
                console.log('Reconfirm Payment API Response:', response);
                dispatch(updateMlmuserObj(response.mlmuserInfo));
                dispatch(setMlmUserObj(response.mlmuserInfo));
                setRefreshingPayment(false);
                setPaymentRefreshError(''); // Clear error on success
            },
            (error) => {
                console.error('Error reconfirming payment:', error);
                setRefreshingPayment(false);
                
                // Set user-friendly error message for inline display
                if (typeof error === 'string') {
                    setPaymentRefreshError(error);
                } else if (error?.message) {
                    setPaymentRefreshError(error.message);
                } else if (error?.response?.data?.message) {
                    setPaymentRefreshError(error.response.data.message);
                } else {
                    setPaymentRefreshError('Failed to reconfirm payment. Please try again.');
                }
            }
        );
    };

    const handleKitDeliveryEdit = () => {
        setTempKitDelivered(userInfo?.kitdelivered || false);
        setApiError(''); // Clear any previous errors
        setKitDeliveryDialogOpen(true);
    };

    const handleKitDeliveryClose = () => {
        setKitDeliveryDialogOpen(false);
        setTempKitDelivered(false);
        setApiError(''); // Clear errors when closing
        setSaving(false);
    };

    const handleKitDeliverySave = () => {
        if (!userInfo) return;

        setSaving(true);
        setApiError(''); // Clear any previous errors

        // Prepare updated user data
        const updatedUserData = {
            kitdelivered: tempKitDelivered
        };

        // Call updateMlmuserInfo API
        updateMlmuserInfo(
            userInfo.id || userInfo.userid,
            updatedUserData,
            (response) => {
                console.log('MLM User Info Update API Response:', response);
                // Update local state with new kit delivered status
                setUserInfo({
                    ...userInfo,
                    kitdelivered: tempKitDelivered
                });
                setKitDeliveryDialogOpen(false);
                setSaving(false);
                dispatch(updateMlmuserObj(response));
                dispatch(setMlmUserObj(response));
                
                console.log('Kit delivery status updated successfully:', tempKitDelivered);
            },
            (error) => {
                console.error('Error updating kit delivery status:', error);
                setSaving(false);
                
                // Set user-friendly error message
                if (typeof error === 'string') {
                    setApiError(error);
                } else if (error?.message) {
                    setApiError(error.message);
                } else if (error?.response?.data?.message) {
                    setApiError(error.response.data.message);
                } else {
                    setApiError('Failed to update kit delivery status. Please try again.');
                }
            }
        );
    };

    const handleAddressEdit = () => {
        setAddressForm({
            address: userInfo?.address || '',
            city: userInfo?.city || '',
            pincode: userInfo?.pincode || ''
        });
        setAddressError('');
        setEditAddressDialogOpen(true);
    };

    const handleAddressDialogClose = () => {
        setEditAddressDialogOpen(false);
        setAddressError('');
    };

    const handleAddressFormChange = (e) => {
        setAddressForm({ ...addressForm, [e.target.name]: e.target.value });
    };

    const handleAddressSave = () => {
        if (!userInfo) return;
        setAddressSaving(true);
        setAddressError('');
        updateAddressAPI(
            {
                userId: userInfo.id || userInfo.userid,
                address: addressForm.address,
                city: addressForm.city,
                pincode: addressForm.pincode
            },
            (response) => {
                setEditAddressDialogOpen(false);
                setAddressSaving(false);
                setUserInfo({
                    ...userInfo,
                    address: addressForm.address,
                    city: addressForm.city,
                    pincode: addressForm.pincode
                });
                dispatch(updateMlmuserObj(response));
                dispatch(setMlmUserObj(response));
            },
            (error) => {
                setAddressSaving(false);
                if (typeof error === 'string') {
                    setAddressError(error);
                } else if (error?.message) {
                    setAddressError(error.message);
                } else if (error?.response?.data?.message) {
                    setAddressError(error.response.data.message);
                } else {
                    setAddressError('Failed to update address. Please try again.');
                }
            }
        );
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };
    
    const getProductKitName = (pkgid) => {
        if (!productList || productList.length === 0) return 'NILL';
        const prod = productList.find(p => p.id === pkgid);
        return prod ? prod.title : '-';
    }

    const renderBasicInfo = (user) => {
        if (!user) {
            return null;
        }

        return (
            <Card sx={{ 
                backgroundColor: 'white', 
                mb: 3,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: 'fit-content'
            }}>
                <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                        Basic Information
                    </Typography>
                    
                    <Grid container direction="row" spacing={2}>
                        {/* Name */}
                        <Grid item lg={12} xs={12}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    NAME
                                </Typography>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700,
                                        mt: 0.5,
                                        color: 'primary.main',
                                        fontSize: '1.3rem'
                                    }}
                                >
                                    {user.title || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* User ID */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    USER ID
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {user.userid || user.id || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Mobile Number */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    MOBILE NUMBER
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'success.main',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {user.mobilenumber || '-'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const renderPersonalInfo = (user) => {
        if (!user) {
            return null;
        }

        return (
            <Card sx={{ 
                backgroundColor: 'white', 
                mb: 3,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: 'fit-content'
            }}>
                <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main', fontWeight: 600 }}>
                        Personal Information
                    </Typography>
                    
                    <Grid container direction="direction" spacing={2}>
                        {/* PAN */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    PAN
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {user.pan || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Aadhaar */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    AADHAAR
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {user.aadharnumber || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* User Type */}
                        <Grid item>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    USER TYPE
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mt: 0.5,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1rem'
                                    }}
                                >
                                    {user.mlmusertypename || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Member Count */}
                        <Grid item>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    MEMBER COUNT
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'info.main',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {user.membercount || '-'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const renderPaymentInfo = (user) => {
        if (!user) {
            return null;
        }

        return (
            <Card sx={{ 
                backgroundColor: 'white', 
                mb: 3,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: 'fit-content'
            }}>
                <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                            Payment Information
                        </Typography>
                        <IconButton 
                            size="small" 
                            onClick={handlePaymentRefresh}
                            disabled={refreshingPayment || !user.paymentorderid}
                            sx={{ 
                                color: (refreshingPayment || !user.paymentorderid) ? 'text.disabled' : 'success.main',
                                '&:hover': {
                                    backgroundColor: (refreshingPayment || !user.paymentorderid) ? 'transparent' : 'success.light',
                                    color: (refreshingPayment || !user.paymentorderid) ? 'text.disabled' : 'white'
                            },
                            '&.Mui-disabled': {
                                color: 'text.disabled'
                            }
                        }}
                        >
                            {refreshingPayment ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <RefreshIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Box>

                    {/* Payment Refresh Error Alert */}
                    {paymentRefreshError && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 2,
                                '& .MuiAlert-message': {
                                    fontSize: '0.875rem'
                                }
                            }}
                            onClose={() => setPaymentRefreshError('')}
                        >
                            {paymentRefreshError}
                        </Alert>
                    )}
                    
                    <Grid container direction="row" spacing={2}>
                        {/* Payment Order ID */}
                        {user.paymentorderid && (
                            <Grid item lg={12} xs={12}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                        PAYMENT ORDER ID
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mt: 0.5, 
                                            fontFamily: 'monospace', 
                                            fontSize: '0.9rem', 
                                            fontWeight: 500,
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {user.paymentorderid}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Payment ID */}
                        {user.paymentid && (
                            <Grid item lg={12} xs={12}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                        PAYMENT ID
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mt: 0.5, 
                                            fontFamily: 'monospace', 
                                            fontSize: '0.9rem', 
                                            fontWeight: 500,
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {user.paymentid}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Payment Amount */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    PAYMENT AMOUNT
                                </Typography>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: 'success.main',
                                        mt: 0.5,
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    {user.paymentamount ? formatAmount(user.paymentamount) : '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Payment Status */}
                        <Grid item lg={6} xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    PAYMENT STATUS
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {user.paymentstatus ? (
                                        <Chip
                                            label={user.paymentstatus?.toUpperCase() || 'UNKNOWN'}
                                            color={user.paymentstatus?.toLowerCase() === 'paid' ? 'success' : 'default'}
                                            size="medium"
                                            sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                                        />
                                    ) : '-'}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    const renderAssociationStatus = (user) => {
        if (!user) {
            return null;
        }

        return (
            <Card sx={{ 
                backgroundColor: 'white', 
                mb: 3,
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: 'fit-content'
            }}>
                <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'warning.main', fontWeight: 600 }}>
                        Association Status
                    </Typography>
                    
                    <Grid container direction="row" spacing={2}>
                        {/* Package ID */}
                        <Grid item lg={12} xs={12}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    Product KIT
                                </Typography>
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        mt: 0.5, 
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    {getProductKitName(user.pkgid) || '-'}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Partnership Status */}
                        <Grid item lg={12} xs={12}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                    PARTNERSHIP STATUS
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={user.partnershipstatus || 'Pending'}
                                        color={user.partnershipstatus === 'CONFIRMED' ? 'success' : 'default'}
                                        size="medium"
                                        sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        {/* Kit Delivered */}
                        <Grid item lg={12} xs={12}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                        KIT DELIVERED
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleKitDeliveryEdit}
                                        disabled={user.partnershipstatus !== 'CONFIRMED'}
                                        sx={{ 
                                            ml: 1, 
                                            color: user.partnershipstatus !== 'CONFIRMED' ? 'text.disabled' : 'primary.main',
                                            '&:hover': {
                                                backgroundColor: user.partnershipstatus !== 'CONFIRMED' ? 'transparent' : 'primary.light',
                                                color: user.partnershipstatus !== 'CONFIRMED' ? 'text.disabled' : 'white'
                                            },
                                            '&.Mui-disabled': {
                                                color: 'text.disabled'
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={user.kitdelivered ? 'YES' : 'NO'}
                                        color={user.kitdelivered ? 'success' : 'warning'}
                                        size="medium"
                                        sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        {/* Address */}
                        <Grid item lg={12} xs={12}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                        ADDRESS
                                    </Typography>
                                    <IconButton 
                                        size="small" 
                                        onClick={handleAddressEdit}
                                        sx={{ 
                                            ml: 1, 
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.light',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mt: 0.5,
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1rem',
                                        lineHeight: 1.4
                                    }}
                                >
                                    {user.address}
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '1rem',
                                        lineHeight: 1.4
                                    }}
                                >
                                    {user.city + " - " + user.pincode}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    return (
        <>
            <Box sx={{ 
                height: '75vh', 
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Grid container sx={{ p: 2, flex: 1 }}>
                    <Grid item xs={12}>
                        {/* Loading State */}
                        {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                <CircularProgress sx={{ mr: 2 }} />
                                <Typography variant="body1" color="text.secondary">
                                    Loading user information...
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {/* First Column - Basic Information */}
                                <Grid item xs={12} lg={3}>
                                    {renderBasicInfo(userInfo)} 
                                </Grid>
                                
                                {/* Second Column - Personal Information */}
                                <Grid item xs={12} lg={3}>  
                                    {renderPersonalInfo(userInfo)}
                                </Grid>
                                
                                {/* Third Column - Payment Information */}
                                <Grid item xs={12} lg={3}>
                                    {renderPaymentInfo(userInfo)}
                                </Grid>
                                
                                {/* Fourth Column - Association Status */}
                                <Grid item xs={12} lg={3}>
                                    {renderAssociationStatus(userInfo)}
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {/* Kit Delivery Status Dialog */}
            <Dialog 
                open={kitDeliveryDialogOpen} 
                onClose={handleKitDeliveryClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ pb: 1, px: 3, pt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            KIT DELIVERED
                        </Typography>
                        <IconButton 
                            onClick={handleKitDeliveryClose}
                            size="small"
                            sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'grey.100',
                                    color: 'text.primary'
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                
                <Divider sx={{ mx: 3 }} />
                
                <DialogContent sx={{ px: 3, pt: 3 }}>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <FormLabel 
                            component="legend" 
                            sx={{ 
                                fontSize: '0.9rem', 
                                fontWeight: 600, 
                                color: 'text.primary',
                                mb: 2
                            }}
                        >
                            Has the kit been delivered to the user?
                        </FormLabel>
                        <RadioGroup
                            value={tempKitDelivered}
                            onChange={(e) => setTempKitDelivered(e.target.value === 'true')}
                            sx={{ ml: 1 }}
                            disabled={saving}
                        >
                            <FormControlLabel 
                                value={true} 
                                control={<Radio color="success" />} 
                                label={
                                    <Typography sx={{ fontWeight: 500 }}>
                                        Yes - Kit has been delivered
                                    </Typography>
                                } 
                            />
                            <FormControlLabel 
                                value={false} 
                                control={<Radio color="warning" />} 
                                label={
                                    <Typography sx={{ fontWeight: 500 }}>
                                        No - Kit not yet delivered
                                    </Typography>
                                } 
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Error Alert */}
                    {apiError && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mt: 2,
                                '& .MuiAlert-message': {
                                    fontSize: '0.875rem'
                                }
                            }}
                            onClose={() => setApiError('')}
                        >
                            {apiError}
                        </Alert>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleKitDeliveryClose} 
                        color="inherit"
                        sx={{ fontWeight: 600 }}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleKitDeliverySave} 
                        variant="contained" 
                        color="primary"
                        sx={{ fontWeight: 600, minWidth: 100 }}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Address Edit Dialog */}
            <Dialog 
                open={editAddressDialogOpen} 
                onClose={handleAddressDialogClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ pb: 1, px: 3, pt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}
                        >
                            Edit Address
                        </Typography>
                        <IconButton 
                            onClick={handleAddressDialogClose}
                            size="small"
                            sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'grey.100',
                                    color: 'text.primary'
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider sx={{ mx: 3 }} />
                <DialogContent sx={{ px: 3, pt: 3 }}>
                    <TextField
                        label="Address"
                        name="address"
                        value={addressForm.address}
                        onChange={handleAddressFormChange}
                        fullWidth
                        margin="normal"
                        disabled={addressSaving}
                    />
                    <TextField
                        label="City"
                        name="city"
                        value={addressForm.city}
                        onChange={handleAddressFormChange}
                        fullWidth
                        margin="normal"
                        disabled={addressSaving}
                    />
                    <TextField
                        label="Pincode"
                        name="pincode"
                        value={addressForm.pincode}
                        onChange={handleAddressFormChange}
                        fullWidth
                        margin="normal"
                        disabled={addressSaving}
                    />
                    {addressError && (
                        <Alert 
                            severity="error" 
                            sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '0.875rem' } }}
                            onClose={() => setAddressError('')}
                        >
                            {addressError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleAddressDialogClose} 
                        color="inherit"
                        sx={{ fontWeight: 600 }}
                        disabled={addressSaving}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddressSave} 
                        variant="contained" 
                        color="primary"
                        sx={{ fontWeight: 600, minWidth: 100 }}
                        disabled={addressSaving}
                        startIcon={addressSaving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {addressSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}